import React, { FunctionComponent, ComponentProps, useMemo } from 'react';
import { DateUtils } from 'record-it-utils/utils/DateUtils/DateUtils';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { classes } from '../../utils/utils';
import styles from './VerticalTimeLine.module.css';
export type VerticalTimeLineProps = PropsWithoutChildren<ComponentProps<'div'>> & {
  /**
   * overlapping entries are not supported yet, so make sure that they don't overlap
   */
  entries: Array<VerticalTimeLineEntry>;
};
export type VerticalTimeLineEntry = {
  key: React.Key;
  startDate: Date;
  endDate: Date;
  renderContent: () => React.JSX.Element;
};
export const VerticalTimeLine: FunctionComponent<VerticalTimeLineProps> = React.memo(props => {
  const {
    entries,
    className,
    ...remainingProps
  } = prepareProps({
    props,
    ignoreChildren: true
  });
  const sortedEntries = useMemo(() => {
    return [...entries].sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  }, [entries]);
  const rows = useMemo<Array<TimeRow>>(() => {
    const newRows: Array<TimeRow> = [];
    const boundaries = getTimeRowBoundaries(sortedEntries);
    if (boundaries) {
      for (let date = new Date(boundaries.startDate); date.getTime() <= boundaries.endDate.getTime(); date.setHours(date.getHours() + 1)) {
        newRows.push({
          date: new Date(date),
          short: false
        });
      }
    }
    return newRows;
  }, [sortedEntries]);
  const entriesToRender = useMemo<Array<VerticalTimeLineEntryRenderData>>(() => {
    const startDate = rows[0]?.date;
    return sortedEntries.map<VerticalTimeLineEntryRenderData>(entry => {
      const fromOffset = startDate ? getHoursOffset(startDate, entry.startDate) : 0;
      const toOffset = startDate ? getHoursOffset(startDate, entry.endDate) : 0;
      return {
        entry,
        fromOffset,
        duration: toOffset - fromOffset
      };
    });
  }, [rows, sortedEntries]);
  return <div {...remainingProps} className={classes(className, styles.container)}>
        <div className={styles.timeLine}>
          {rows.map((row, index) => <div
      // since we have no internal state here, we can use the index safely
      // eslint-disable-next-line react/no-array-index-key
      key={index} className={classes(styles.timeRow, index === rows.length - 1 ? styles.lastTimeRow : null)} data-short={row.short ? 'true' : 'false'} data-testid="VerticalTimeLine.TimeRow">
              <div className={styles.timeRowSeparator}></div>
              <div className={styles.timeRowLabel} data-testid="VerticalTimeLine.TimeRowLabel">
                {DateUtils.withCurrentTimezone().formatTimeString(row.date)}
              </div>
            </div>)}
          {entriesToRender.map(renderInfo => <div key={renderInfo.entry.key} className={styles.timeLineEntry} style={{
        '--offset': renderInfo.fromOffset,
        '--duration': renderInfo.duration
      } as any} data-testid="VerticalTimeLine.TimeLineEntry">
              {renderInfo.entry.renderContent()}
            </div>)}
        </div>
      </div>;
});
function getTimeRowBoundaries(sortedEntries: Array<VerticalTimeLineEntry>): TimeRowBoundaries | null {
  const firstEntry = sortedEntries[0];
  const lastEntry = sortedEntries[sortedEntries.length - 1];
  if (!firstEntry || !lastEntry) {
    return null;
  }
  const startDate = new Date(firstEntry.startDate);
  startDate.setMinutes(0);
  startDate.setSeconds(0);
  startDate.setMilliseconds(0);
  const endDate = new Date(lastEntry.endDate);
  endDate.setMinutes(0);
  endDate.setSeconds(0);
  endDate.setMilliseconds(0);

  // edge case: the lastEntry.endDate ends at an exact hour, so we don't have to add an extra one here
  if (endDate.getTime() !== lastEntry.endDate.getTime()) {
    endDate.setHours(endDate.getHours() + 1);
  }
  return {
    startDate,
    endDate
  };
}
function getHoursOffset(from: Date, to: Date): number {
  const diff = to.getTime() - from.getTime();
  return diff / (60 * 60 * 1000);
}
type TimeRowBoundaries = {
  startDate: Date;
  endDate: Date;
};
type TimeRow = {
  date: Date;
  short: boolean;
};
type VerticalTimeLineEntryRenderData = {
  entry: VerticalTimeLineEntry;
  /** in hours */
  fromOffset: number;
  /** in hours */
  duration: number;
};